import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import SkeletonTikTokAdPreviewCardItem from './Skeleton'
import AdCardListItem from '../../../../../features/components/AdCardListItem'
import ActionsDropdown from '../../../../../features/components/ActionsDropdown'
import AdTikTokPreview from '../../../../../forms/Tiktok/AdForms/AdTikTokEdit/AdTikTokPreview'
import AdCardDetails from '../../../../../features/components/AdCardListItem/AdCardDetails'
import TikTokAdPreviewCardMetrics from './TikTokAdPreviewCardMetrics'
import InternalAdActionButtons from '../../../../AdApprovals/AdsSection/InternalAdActionButtons'
import AdStatusChip from '../../FacebookAdsPreviewList/FacebookAdCardListItem/AdCardStatuses/AdStatusChip'

import useGetAdDropdownOptions from '../../hooks/useGetAdDropdownOptions'
import useInternalAdSelfAccountName from '../../../../../features/hooks/internalAdHooks/useInternalAdSelfAccountName'
import { useClearApproveInternalAd } from '../../../../AdApprovals/AdsSection/hooks'

import { approveInternalAd } from '../../../../../modules/actions/ads'
import { openForm } from '../../../../../modules/actions/forms'
import { selectedTikTokAdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import {
  adUpdateIdSelector,
  adUpdateIsLoadingSelector,
  approveInternalAdIsLoadingSelector,
  disapproveInternalAdIsLoadingSelector
} from '../../../../../modules/selectors/ads'

import { TIKTOK_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { AD_TIK_TOK_EDIT } from '../../../../../constants/forms'

import useStyles from './styles'

const TikTokAdPreviewCardItem = props => {
  const classes = useStyles()

  const dispatch = useDispatch()

  const { id: tiktokAdAccountId, currency_symbol: currencySymbol } = useSelector(selectedTikTokAdAccountSelector)
  const adUpdateId = useSelector(adUpdateIdSelector)
  const adUpdateIsLoading = useSelector(adUpdateIsLoadingSelector)

  const {
    ad,
    showStatusChip = true,
    showActionsDropdown = true,
    showDetails = true,
    showMetrics = true,
    showInternalAdActionButtons,
    showConversionsMetric,
    showAdditionalMetrics,
    disapproveInternalAdOpenModalHandler
  } = props

  const { id, name } = ad

  const adSelfAccountName = useInternalAdSelfAccountName(ad)

  // On internal ad approvals page - we use selfAccountName which uploaded the ad for details subTitle
  const subTitle = showInternalAdActionButtons ? adSelfAccountName : ''

  const adItemIsUpdating = adUpdateIsLoading && adUpdateId === id
  const internalAdIsApproving = useSelector(approveInternalAdIsLoadingSelector(id))
  const internalAdIsDisapproving = useSelector(disapproveInternalAdIsLoadingSelector(id))
  useClearApproveInternalAd(ad.id)

  const getAdDropdownOptions = useGetAdDropdownOptions(TIKTOK_PLATFORM, tiktokAdAccountId)
  const actionsDropdownOptions = getAdDropdownOptions(ad)

  const approveInternalAdHandler = useCallback(() => {
    dispatch(approveInternalAd(id))
  }, [dispatch, id])

  const editInternalAdHandler = useCallback(() => {
    dispatch(openForm({ id, formName: AD_TIK_TOK_EDIT, otherParams: 'internal=true' }))
  }, [dispatch, id])

  const disapproveInternalAdHandler = useCallback(() => {
    disapproveInternalAdOpenModalHandler(ad)
  }, [ad, disapproveInternalAdOpenModalHandler])

  if (adItemIsUpdating || internalAdIsApproving || internalAdIsDisapproving) {
    return <SkeletonTikTokAdPreviewCardItem {...props} />
  }

  return (
    <AdCardListItem>
      {(showStatusChip || showActionsDropdown) && (
        <div className={classes.cardHeader}>
          {showStatusChip && <AdStatusChip ad={ad} />}
          {showActionsDropdown && actionsDropdownOptions.length && (
            <ActionsDropdown
              options={actionsDropdownOptions}
              actionsListWrapperClassName={classes.actionsListWrapper}
              className={classes.actionsDropdownMenu}
              closeOnInsideClick
            />
          )}
        </div>
      )}
      <div className={classes.adPreviewContainer}>
        <AdTikTokPreview adData={ad} className={classes.adPreview} />
      </div>
      <div className={classes.previewContainer} />
      {showDetails && <AdCardDetails title={name} subTitle={subTitle} />}
      {showMetrics && (
        <TikTokAdPreviewCardMetrics
          ad={ad}
          currencySymbol={currencySymbol}
          showConversionsMetric={showConversionsMetric}
          showAdditionalMetrics={showAdditionalMetrics}
        />
      )}
      {showInternalAdActionButtons && (
        <InternalAdActionButtons
          approveInternalAdHandler={approveInternalAdHandler}
          editInternalAdHandler={editInternalAdHandler}
          disapproveInternalAdHandler={disapproveInternalAdHandler}
        />
      )}
    </AdCardListItem>
  )
}

TikTokAdPreviewCardItem.propTypes = {
  ad: PropTypes.object.isRequired,
  showStatusChip: PropTypes.bool,
  showActionsDropdown: PropTypes.bool,
  showDetails: PropTypes.bool,
  showMetrics: PropTypes.bool,
  showInternalAdActionButtons: PropTypes.bool,
  showConversionsMetric: PropTypes.bool,
  showAdditionalMetrics: PropTypes.bool,
  disapproveInternalAdOpenModalHandler: PropTypes.func
}

export default TikTokAdPreviewCardItem
